import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bot, CalendarDays, Clock, User, ArrowLeft, CheckCircle, Lightbulb, Target } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

type Language = 'es' | 'en';

const ImplementarIAMarketing = () => {
  const [language, setLanguage] = useState<Language>('es');

  const toggleLanguage = (lang: Language) => {
    setLanguage(lang);
  };

  const content = {
    es: {
      title: "Cómo Implementar IA en tu Estrategia de Marketing Digital 2025 | Luis ROC",
      description: "Guía completa para implementar inteligencia artificial en marketing digital. Herramientas de IA, casos de éxito reales y plan paso a paso para automatizar tu negocio en 2025.",
      keywords: "implementar IA marketing, ChatGPT marketing, automatización marketing digital, herramientas IA marketing, Google Ads IA, marketing automation 2025",
      url: "https://luisroc.com/blog/implementar-ia-marketing-digital",
      articleTitle: "Cómo Implementar IA en tu Estrategia de Marketing Digital",
      excerpt: "Descubre las herramientas de IA más efectivas para automatizar y mejorar tus campañas de marketing, desde chatbots hasta análisis predictivo.",
      publishDate: "15 de mayo, 2025",
      publishDateISO: "2025-05-15T10:00:00-05:00",
      modifiedDateISO: "2025-05-15T10:00:00-05:00",
      readTime: "8 minutos de lectura",
      author: "Luis ROC",
      backButton: "← Volver al Blog",
      tags: ["IA", "Marketing", "Automatización", "ChatGPT"],
      breadcrumbs: [
        { name: "Inicio", url: "https://luisroc.com" },
        { name: "Blog", url: "https://luisroc.com/blog" },
        { name: "Implementar IA en Marketing", url: "https://luisroc.com/blog/implementar-ia-marketing-digital" }
      ],
      sections: {
        intro: {
          title: "La Revolución de la IA en Marketing Digital",
          content: [
            "La inteligencia artificial está transformando radicalmente cómo las empresas se conectan con sus clientes. En 2024, las empresas que implementan IA en su marketing digital están viendo aumentos del 37% en conversiones y reducciones del 28% en costos de adquisición de clientes.",
            "Como consultor especializado en marketing digital para el mercado hispano-estadounidense, he visto de primera mano cómo la IA puede ser el diferenciador que necesitas para competir efectivamente en Estados Unidos."
          ]
        },
        tools: {
          title: "Herramientas de IA Esenciales para Marketing",
          items: [
            {
              name: "ChatGPT para Contenido",
              description: "Generación de copy publicitario, emails, posts sociales y blogs optimizados para SEO"
            },
            {
              name: "Claude para Análisis",
              description: "Análisis de datos de campañas, insights de comportamiento del usuario y reportes automatizados"
            },
            {
              name: "Google AI en Ads",
              description: "Optimización automática de pujas, targeting inteligente y creación de anuncios responsivos"
            },
            {
              name: "HubSpot AI",
              description: "Lead scoring predictivo, personalización de contenido y automatización de nurturing"
            }
          ]
        },
        implementation: {
          title: "Plan de Implementación Paso a Paso",
          steps: [
            {
              step: "1. Auditoría de Procesos Actuales",
              description: "Identifica qué tareas consumen más tiempo y son repetitivas en tu marketing actual"
            },
            {
              step: "2. Selección de Herramientas IA",
              description: "Elige 2-3 herramientas máximo para evitar complejidad y enfócate en dominarlas"
            },
            {
              step: "3. Capacitación del Equipo",
              description: "Invierte en training específico para cada herramienta y establece best practices"
            },
            {
              step: "4. Implementación Gradual",
              description: "Comienza con un canal o proceso, mide resultados y expande gradualmente"
            },
            {
              step: "5. Monitoreo y Optimización",
              description: "Establece KPIs claros y ajusta continuamente basado en performance data"
            }
          ]
        },
        cases: {
          title: "Casos de Éxito Reales",
          examples: [
            {
              business: "Bufete de Abogados de Houston",
              challenge: "Generación de leads cualificados para casos de inmigración",
              solution: "Implementé chatbot con IA + Google Ads optimizado con machine learning",
              result: "340% aumento en leads cualificados, 45% reducción en costo por lead"
            },
            {
              business: "E-commerce de Productos Latinos",
              challenge: "Personalización de contenido para mercado bilingüe",
              solution: "IA para traducción contextual + personalización dinámica de producto",
              result: "67% mejora en engagement, 23% aumento en conversión"
            }
          ]
        }
      },
      cta: {
        title: "¿Listo para Implementar IA en tu Marketing?",
        description: "Te ayudo a crear una estrategia personalizada de IA que se adapte a tu negocio y objetivos específicos.",
        button: "Solicitar Consulta Gratuita"
      }
    },
    en: {
      title: "How to Implement AI in Your Digital Marketing Strategy 2025 | Luis ROC",
      description: "Complete guide to implement artificial intelligence in digital marketing. AI tools, real success stories and step-by-step plan to automate your business in 2025.",
      keywords: "implement AI marketing, ChatGPT marketing, digital marketing automation, AI marketing tools, Google Ads AI, marketing automation 2025",
      url: "https://luisroc.com/en/blog/implement-ai-digital-marketing-strategy",
      articleTitle: "How to Implement AI in Your Digital Marketing Strategy",
      excerpt: "Discover the most effective AI tools to automate and improve your marketing campaigns, from chatbots to predictive analytics.",
      publishDate: "May 15, 2025",
      publishDateISO: "2025-05-15T10:00:00-05:00",
      modifiedDateISO: "2025-05-15T10:00:00-05:00",
      readTime: "8 min read",
      author: "Luis ROC",
      backButton: "← Back to Blog",
      tags: ["AI", "Marketing", "Automation", "ChatGPT"],
      breadcrumbs: [
        { name: "Home", url: "https://luisroc.com/en" },
        { name: "Blog", url: "https://luisroc.com/en/blog" },
        { name: "Implement AI in Marketing", url: "https://luisroc.com/en/blog/implement-ai-digital-marketing-strategy" }
      ],
      sections: {
        intro: {
          title: "The AI Revolution in Digital Marketing",
          content: [
            "Artificial intelligence is radically transforming how companies connect with their customers. In 2024, businesses implementing AI in their digital marketing are seeing 37% increases in conversions and 28% reductions in customer acquisition costs.",
            "As a consultant specializing in digital marketing for the Hispanic-American market, I've seen firsthand how AI can be the differentiator you need to compete effectively in the United States."
          ]
        },
        tools: {
          title: "Essential AI Tools for Marketing",
          items: [
            {
              name: "ChatGPT for Content",
              description: "Generation of ad copy, emails, social posts, and SEO-optimized blogs"
            },
            {
              name: "Claude for Analysis",
              description: "Campaign data analysis, user behavior insights, and automated reporting"
            },
            {
              name: "Google AI in Ads",
              description: "Automatic bid optimization, intelligent targeting, and responsive ad creation"
            },
            {
              name: "HubSpot AI",
              description: "Predictive lead scoring, content personalization, and nurturing automation"
            }
          ]
        },
        implementation: {
          title: "Step-by-Step Implementation Plan",
          steps: [
            {
              step: "1. Current Process Audit",
              description: "Identify which tasks consume the most time and are repetitive in your current marketing"
            },
            {
              step: "2. AI Tool Selection",
              description: "Choose 2-3 tools maximum to avoid complexity and focus on mastering them"
            },
            {
              step: "3. Team Training",
              description: "Invest in specific training for each tool and establish best practices"
            },
            {
              step: "4. Gradual Implementation",
              description: "Start with one channel or process, measure results, and gradually expand"
            },
            {
              step: "5. Monitoring and Optimization",
              description: "Establish clear KPIs and continuously adjust based on performance data"
            }
          ]
        },
        cases: {
          title: "Real Success Stories",
          examples: [
            {
              business: "Houston Law Firm",
              challenge: "Generating qualified leads for immigration cases",
              solution: "Implemented AI chatbot + Google Ads optimized with machine learning",
              result: "340% increase in qualified leads, 45% reduction in cost per lead"
            },
            {
              business: "Latino Products E-commerce",
              challenge: "Content personalization for bilingual market",
              solution: "AI for contextual translation + dynamic product personalization",
              result: "67% improvement in engagement, 23% increase in conversion"
            }
          ]
        }
      },
      cta: {
        title: "Ready to Implement AI in Your Marketing?",
        description: "I help you create a personalized AI strategy that adapts to your business and specific objectives.",
        button: "Request Free Consultation"
      }
    }
  };

  const t = content[language];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <meta name="keywords" content={t.keywords} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content={language === 'es' ? 'Spanish' : 'English'} />
        <meta name="author" content="Luis ROC" />
        <meta name="geo.region" content="US" />
        <meta name="geo.country" content="US" />
        <meta name="geo.placename" content="United States" />
        
        <link rel="canonical" href={t.url} />
        
        {/* Open Graph Article */}
        <meta property="og:title" content={t.articleTitle} />
        <meta property="og:description" content={t.description} />
        <meta property="og:url" content={t.url} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="ROC Worldwide Agency LLC" />
        <meta property="og:locale" content={language === 'es' ? 'es_US' : 'en_US'} />
        <meta property="og:image" content="https://luisroc.com/media/luis-roc/portrait.png" />

        <meta property="og:image:alt" content={t.articleTitle} />
        
        {/* Article specific Open Graph */}
        <meta property="article:author" content="Luis ROC" />
        <meta property="article:published_time" content={t.publishDateISO} />
        <meta property="article:modified_time" content={t.modifiedDateISO} />
        <meta property="article:section" content={language === 'es' ? 'Marketing Digital' : 'Digital Marketing'} />
        <meta property="article:tag" content={language === 'es' ? 'Inteligencia Artificial' : 'Artificial Intelligence'} />
        <meta property="article:tag" content="Marketing" />
        <meta property="article:tag" content={language === 'es' ? 'Automatización' : 'Automation'} />
        <meta property="article:tag" content="ChatGPT" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@luisrocmarketing" />
        <meta name="twitter:creator" content="@luisrocmarketing" />
        <meta name="twitter:title" content={t.articleTitle} />
        <meta name="twitter:description" content={t.description} />
        <meta name="twitter:image" content="https://luisroc.com/media/luis-roc/portrait.png" />
        <meta name="twitter:image:alt" content={t.articleTitle} />
        
        {/* Hreflang */}
        
        {/* Preconnect for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* Structured Data - Complex Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BlogPosting",
                "@id": t.url + "#article",
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": t.url
                },
                "headline": t.articleTitle,
                "description": t.description,
                "image": "https://luisroc.com/media/luis-roc/portrait.png",
                "author": {
                  "@id": "https://luisroc.com/#luisroc"
                },
                "publisher": {
                  "@id": "https://luisroc.com/#organization"
                },
                "datePublished": t.publishDateISO,
                "dateModified": t.modifiedDateISO,
                "inLanguage": language === 'es' ? 'es-US' : 'en-US',
                "url": t.url,
                "keywords": t.keywords,
                "articleSection": language === 'es' ? 'Marketing Digital' : 'Digital Marketing',
                "wordCount": 1200,
                "timeRequired": "PT8M",
                "about": [
                  {
                    "@type": "Thing",
                    "name": language === 'es' ? 'Inteligencia Artificial' : 'Artificial Intelligence'
                  },
                  {
                    "@type": "Thing", 
                    "name": language === 'es' ? 'Marketing Digital' : 'Digital Marketing'
                  },
                  {
                    "@type": "Thing",
                    "name": language === 'es' ? 'Automatización de Marketing' : 'Marketing Automation'
                  }
                ],
                "mentions": [
                  {
                    "@type": "SoftwareApplication",
                    "name": "ChatGPT"
                  },
                  {
                    "@type": "SoftwareApplication", 
                    "name": "Google Ads"
                  },
                  {
                    "@type": "SoftwareApplication",
                    "name": "HubSpot"
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": t.breadcrumbs.map((crumb, index) => ({
                  "@type": "ListItem",
                  "position": index + 1,
                  "name": crumb.name,
                  "item": crumb.url
                }))
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": language === 'es' ? "¿Qué herramientas de IA son esenciales para marketing digital?" : "What AI tools are essential for digital marketing?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": language === 'es' ? "Las herramientas más efectivas incluyen ChatGPT para generación de contenido, Claude para análisis de datos, Google AI para optimización de anuncios, y HubSpot AI para lead scoring predictivo." : "The most effective tools include ChatGPT for content generation, Claude for data analysis, Google AI for ad optimization, and HubSpot AI for predictive lead scoring."
                    }
                  },
                  {
                    "@type": "Question", 
                    "name": language === 'es' ? "¿Cómo implementar IA en marketing paso a paso?" : "How to implement AI in marketing step by step?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": language === 'es' ? "El proceso incluye: 1) Auditoría de procesos actuales, 2) Selección de herramientas IA (máximo 2-3), 3) Capacitación del equipo, 4) Implementación gradual, y 5) Monitoreo y optimización continua." : "The process includes: 1) Current process audit, 2) AI tool selection (maximum 2-3), 3) Team training, 4) Gradual implementation, and 5) Continuous monitoring and optimization."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Header language={language} onLanguageChange={toggleLanguage} />
      
      <main className="min-h-screen bg-gradient-to-b from-background via-background/95 to-background/90">
        <article className="container mx-auto px-4 py-16 max-w-4xl">
          {/* Back Button */}
          <div className="mb-8">
            <Button variant="ghost" asChild className="text-muted-foreground hover:text-foreground">
              <Link to={language === 'es' ? '/blog' : '/en/blog'}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t.backButton}
              </Link>
            </Button>
          </div>

          {/* Article Header */}
          <header className="mb-12 text-center">
            <div className="flex justify-center mb-6">
              <Badge variant="secondary" className="flex items-center gap-2 px-4 py-2">
                <Bot className="h-4 w-4" />
                IA & Marketing
              </Badge>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent leading-tight">
              {t.articleTitle}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto">
              {t.excerpt}
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center">
                <User className="h-4 w-4 mr-2" />
                {t.author}
              </div>
              <div className="flex items-center">
                <CalendarDays className="h-4 w-4 mr-2" />
                {t.publishDate}
              </div>
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                {t.readTime}
              </div>
            </div>

            <div className="flex justify-center gap-2 mb-8">
              {t.tags.map((tag, index) => (
                <Badge key={index} variant="outline">{tag}</Badge>
              ))}
            </div>
          </header>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-12">
              <Card className="card-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-2xl">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    {t.sections.intro.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {t.sections.intro.content.map((paragraph, index) => (
                    <p key={index} className="text-muted-foreground leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </CardContent>
              </Card>
            </section>

            {/* Tools Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <Bot className="h-7 w-7 text-primary" />
                {t.sections.tools.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {t.sections.tools.items.map((tool, index) => (
                  <Card key={index} className="card-elegant">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-primary" />
                        {tool.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{tool.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Implementation Steps */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <Target className="h-7 w-7 text-primary" />
                {t.sections.implementation.title}
              </h2>
              <div className="space-y-6">
                {t.sections.implementation.steps.map((step, index) => (
                  <Card key={index} className="card-elegant">
                    <CardContent className="pt-6">
                      <div className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg mb-2">{step.step}</h3>
                          <p className="text-muted-foreground">{step.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Success Cases */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-8">{t.sections.cases.title}</h2>
              <div className="space-y-8">
                {t.sections.cases.examples.map((example, index) => (
                  <Card key={index} className="card-elegant">
                    <CardHeader>
                      <CardTitle className="text-xl text-primary">{example.business}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">Desafío:</h4>
                        <p className="text-muted-foreground">{example.challenge}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Solución:</h4>
                        <p className="text-muted-foreground">{example.solution}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Resultado:</h4>
                        <p className="text-primary font-medium">{example.result}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </div>

          {/* CTA Section */}
          <section className="mt-16">
            <Card className="card-elegant bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
              <CardContent className="pt-8 text-center">
                <h3 className="text-2xl font-bold mb-4">{t.cta.title}</h3>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  {t.cta.description}
                </p>
                <Button size="lg" asChild className="shadow-lg hover:shadow-xl transition-all duration-300">
                  <Link to={language === 'es' ? '/contacto' : '/en/contact'}>
                    {t.cta.button}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </section>
        </article>
      </main>
      
      <Footer language={language} />
    </>
  );
};

export default ImplementarIAMarketing;